<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router';
import Snackbar from '@/components/Shared/Snackbar/Snackbar.vue';
import MobileAppBar from '@/components/Shared/AppBar/MobileAppBar/MobileAppBar.vue';
import MobileDrawer from '@/components/Shared/AppBar/MobileDrawer/MobileDrawer.vue';
import MobileTopBar from '@/components/Mobile/Shell/MobileTopBar.vue';
import DesktopDrawer from '@/components/Shared/AppBar/DesktopDrawer/DesktopDrawer.vue';
import HelpDrawer from '@/components/Shared/HelpDrawer/HelpDrawer.vue';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import UnsubscribedGate from '@/components/Shared/Gates/UnsubscribedGate/UnsubscribedGate.vue';
import LaborMarketReportsModal from '@/components/LaborMarket/LaborMarketReportsModal.vue';
import UpgradeToGoldModal from '@/components/Shared/Modals/UpgradeToGoldModal.vue';
import GoldUpgradeConfirmedModal from '@/components/Shared/Modals/GoldUpgradeConfirmedModal.vue';
import ApplicantFlowInsightsSuccessModal from '@/components/Insights/ApplicantFlowInsightsSuccessModal.vue';
import JobMatchReportSuccessModal from '@/components/LaborMarket/JobMatchReportSuccessModal.vue';
import UploadResumesModal from '@/components/Shared/Projects/ProjectCard/UploadResumesModal.vue';
import { useShowMobileView } from '@/composables/useShowMobileView';
import { computed, onUnmounted, watch } from 'vue';

const subscriptionService = new SubscriptionService();
const isEmployerSubscribed = computed(() => subscriptionService.isEmployerSubscribed);
const isSubscriptionLoaded = computed(() => subscriptionService.isSubscriptionLoaded);

// On a phone the /jobs mobile experience renders its own MobileTopBar, so hide the
// legacy chrome there to avoid two stacked top bars. Desktop and other routes are
// unaffected (copilot-activation lives under a different layout).
const route = useRoute();
const showMobileView = useShowMobileView();
// Match /jobs and its sub-routes only, not lookalikes like /jobseekers.
const isMobileJobsView = computed(() => showMobileView.value && /^\/jobs(\/|$)/.test(route.path));

// Chameleon injects a help widget (#chmln-dom) at the bottom of <body>, outside the
// Vue tree. In the full-screen mobile shell its help bar adds a strip below the
// viewport, making the page scroll. Toggle a body class the global stylesheet keys off
// to hide it here; desktop and "Show full version" keep the widget.
const MOBILE_SHELL_BODY_CLASS = 'mobile-shell-active';
// viewport-fit=cover is applied only while the mobile shell is active: it is what
// makes env(safe-area-inset-*) non-zero, and only the new shell pads for the notch
// and home indicator. Legacy phone routes keep the default viewport so they are not
// drawn under device cutouts.
const VIEWPORT_META_BASE = 'width=device-width, initial-scale=1.0';
function setViewportFitCover(active: boolean): void {
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      active ? `${VIEWPORT_META_BASE}, viewport-fit=cover` : VIEWPORT_META_BASE,
    );
}
watch(
  isMobileJobsView,
  (active) => {
    // Guarded for safety: the immediate run happens during setup, before mount.
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.toggle(MOBILE_SHELL_BODY_CLASS, active);
    setViewportFitCover(active);
  },
  { immediate: true },
);
onUnmounted(() => {
  document.body.classList.remove(MOBILE_SHELL_BODY_CLASS);
  setViewportFitCover(false);
});
</script>

<template>
  <!-- Mobile Shared App Bar -->
  <MobileAppBar v-if="!isMobileJobsView" />

  <!-- Mobile Shared Navigation Drawer -->
  <MobileDrawer v-if="!isMobileJobsView" />

  <!-- Desktop Shared Navigation Drawer -->
  <DesktopDrawer />

  <!-- Help drawer, opened from the left nav's ? button -->
  <HelpDrawer />

  <!-- Labor Market Reports Modal -->
  <LaborMarketReportsModal />
  <!-- Applicant Flow Insights Success Modal -->
  <ApplicantFlowInsightsSuccessModal />
  <!-- Job Match Report Success Modal (single-candidate quick request) -->
  <JobMatchReportSuccessModal />

  <!-- Upgrade to Gold Modal -->
  <UpgradeToGoldModal />

  <!-- Gold Upgrade Confirmed Modal -->
  <GoldUpgradeConfirmedModal />

  <!-- Upload Resumes Modal -->
  <UploadResumesModal />

  <!-- Main Router View -->
  <v-main>
    <!-- Subscribed users -->
    <template v-if="isEmployerSubscribed">
      <Snackbar />
      <RouterView />
    </template>

    <!-- Unsubscribed users. Gated on the fetch having completed: an unloaded
         subscription only means "unknown" and must not flash the expired screen. -->
    <template v-else-if="isSubscriptionLoaded">
      <!-- On a phone the mobile /jobs experience (which renders the top bar) is not
           mounted while expired, so render the mobile top bar here too. Otherwise a
           multi-employer recruiter whose selected org's plan expired has no way to
           open the org switcher and switch back to an org with an active plan. -->
      <div v-if="isMobileJobsView" class="flex h-full flex-col" data-test="mobile-unsubscribed">
        <MobileTopBar />
        <div class="min-h-0 flex-1">
          <UnsubscribedGate />
        </div>
      </div>
      <UnsubscribedGate v-else />
    </template>
  </v-main>
</template>
